import { isEmpty, partition, values } from "lodash";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useInstalls } from "../../../context/installs";
import { useLocations } from "../../../context/locations";
import { Install } from "../../../utils/types";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";
import LinkedInstallsTable from "../common/LinkedInstallsTable";

const LocationInstallsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { currentLocation, locations } = useLocations();
  const { installs, toggleInstallSidebar } = useInstalls();

  const location = useMemo(
    () =>
      currentLocation ||
      (props.match.params.id ? locations[props.match.params.id] : undefined),
    [currentLocation, locations, props.match.params.id]
  );

  const [currentlyInstalled, notCurrentlyInstalled] = useMemo(
    () =>
      partition(
        values(installs).filter(
          ({ location_uuid }) => location_uuid === props.match.params.id
        ),
        isActiveInstall
      ),
    [installs, props.match.params.id]
  );

  const futureInstalls = useMemo(
    () =>
      values(installs).filter(
        ({ location_uuid, installed_at }) =>
          location_uuid === props.match.params.id && !installed_at
      ),
    [installs, props.match.params.id]
  );

  const emptyInstall: Install = useMemo(
    () => ({
      uuid: "",
      created_by: "",
      created_at: "",
      updated_by: "",
      updated_at: "",
      hotspot_uuid: "",
      location_uuid: props.match.params.id ?? "",
      installed_at: "",
      uninstalled_at: "",
      commissions: []
    }),
    [props.match.params.id]
  );

  if (!location) return null;

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="w-full mb-6 align-middle inline-block">
        <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-500 pb-5 mb-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Current installs
          </h3>
          <button
            type="button"
            className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={() => toggleInstallSidebar(emptyInstall)}
          >
            Add Install
          </button>
        </div>
        {!isEmpty(currentlyInstalled) ? (
          <LinkedInstallsTable installs={currentlyInstalled} />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-300">
            There are no active installs for this location.
          </p>
        )}
      </div>

      <div className="w-full mb-6 align-middle inline-block">
        <div className="border-b border-gray-200 dark:border-gray-500 pb-5 mb-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Past installs
          </h3>
        </div>
        {!isEmpty(notCurrentlyInstalled) ? (
          <LinkedInstallsTable installs={notCurrentlyInstalled} />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-300">
            There are no past installs for this location.
          </p>
        )}
      </div>

      <div className="w-full mb-6 align-middle inline-block">
        <div className="border-b border-gray-200 dark:border-gray-500 pb-5 mb-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Future installs
          </h3>
        </div>
        {!isEmpty(futureInstalls) ? (
          <LinkedInstallsTable installs={futureInstalls} />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-300">
            There are no future installs for this location.
          </p>
        )}
      </div>
    </div>
  );
};

export default LocationInstallsTab;
